import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, ScrollView } from 'react-native';

import styles from './mc-activity.style';
import { TemplateType } from '../../../../enums/template-type';
import { useColors } from '../../../../hooks';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import { useErrorSound } from '../../../../hooks/use-error-sound';
import { useScroll } from '../../../../hooks/use-scroll';
import { useSuccessSound } from '../../../../hooks/use-success-sound';
import useVibration from '../../../../hooks/use-vibration';
import { arrayShuffle } from '../../../../utils/array';
import KeyboardLegend, {
  KeyboardLegendPadding,
} from '../../../keyboard-legend/keyboard-legend';
import LessonAccentBanner from '../../../lesson-accent-banner/lesson-accent-banner';
import LessonImage from '../../../lesson-image/lesson-image';
import LessonInstructions from '../../../lesson-instructions/lesson-instructions';
import LessonPlayerFrame from '../../../lesson-player-frame/lesson-player-frame';
import { OptionChooseContainer } from '../../../option-choose-container/option-choose-container';
import { StickAudioPlayer } from '../../../stick-audio-player/stick-audio-player';
import ThemedText2 from '../../../themed-text-2';

interface IncorrectAnswerInterface {
  incorrectAnswer: string;
  incorrectFeedback: string;
  incorrectFeedbackTranslation?: string;
}

interface MultipleChoiceActivityInterface {
  type: 'MC' | 'MC - EXTENDED';
  instructions: string;
  instructionsTranslation?: string;
  question: string;
  image: string;
  audio?: string;
  correctAnswer: string;
  correctFeedback: string;
  correctFeedbackTranslation?: string;
  steps: IncorrectAnswerInterface[];
}

export function MultipleChoiceActivity() {
  const colors = useColors();
  const { t } = useTranslation();
  const activityContext = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const { vibrate } = useVibration();
  const { completeCurrentActivity, isCurrentActivityComplete } =
    activityContext;
  const scrollRef = useRef<ScrollView>(null);
  const currentActivity =
    activityContext.currentActivity as MultipleChoiceActivityInterface;
  const incorrectAnswers = currentActivity.steps;
  const { scrollToEnd } = useScroll(scrollRef);
  const [optionsOpacity] = useState(new Animated.Value(1));
  const [hasSelectedOption, setSelectedOption] = useState(false);
  const [isComplete, setComplete] = useState(false);
  const [index, setIndex] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;

  const options = useMemo(() => {
    return arrayShuffle([
      ...incorrectAnswers.map(({ incorrectAnswer }) => incorrectAnswer),
      currentActivity.correctAnswer,
    ]) as string[];
  }, [incorrectAnswers, currentActivity.correctAnswer]);

  const handleReset = useCallback(() => {
    const fadeIn = Animated.timing(optionsOpacity, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    });

    const fadeOut = Animated.timing(optionsOpacity, {
      toValue: 0,
      duration: 400,
      useNativeDriver: true,
    });

    fadeOut.start(({ finished }) => {
      if (finished) {
        setSelectedOption(false);
        setIndex((x) => x + 1);
        fadeIn.start();
      }
    });
  }, [optionsOpacity]);

  function handleAnswer(correct: boolean) {
    if (correct) {
      correctSound.play();
      completeCurrentActivity();
      setComplete(true);
    } else {
      incorrectSound.play();
      vibrate();
      setTimeout(() => {
        handleReset();
      }, 2000);
    }
    setSelectedOption(true);
  }

  return (
    <>
      <LessonPlayerFrame scrollY={scrollY} showContinueButton={isComplete}>
        <LessonAccentBanner>
          <LessonInstructions
            translation={currentActivity.instructionsTranslation}
          >
            {currentActivity.instructions}
          </LessonInstructions>
          <ThemedText2
            style={styles.question}
            fontSize={24}
            type="onPrimary"
            fontWeightStyle="bold"
          >
            {currentActivity.question}
          </ThemedText2>
        </LessonAccentBanner>
        {!!currentActivity.audio && (
          <StickAudioPlayer
            scrollY={scrollY}
            isSlowButtonVisible
            isTextProgressVisible
            containerStyle={styles.audioPlayer}
            barContainerColor={colors.primaryOpacity}
            inactiveColor={colors.onPrimary}
            activeColor={colors.onPrimary}
            url={currentActivity.audio}
          />
        )}
        <LessonImage imageUrl={currentActivity.image} style={styles.image} />
        <Animated.View style={{ opacity: optionsOpacity }}>
          <OptionChooseContainer
            index={index}
            containerStyle={styles.optionContainer}
            disabled={hasSelectedOption}
            answer={currentActivity.correctAnswer}
            options={options}
            onAnswer={handleAnswer}
            scrollToEnd={scrollToEnd}
            checkButtonActive={false}
          />
        </Animated.View>
        {!isComplete && <KeyboardLegendPadding />}
      </LessonPlayerFrame>
      <KeyboardLegend
        templateType={TemplateType.MC}
        forceHide={hasSelectedOption || isCurrentActivityComplete}
        text={t('keyboardLegend.answer')}
        indicatorList={options.map((_, i) => (i + 1).toString())}
      />
    </>
  );
}
